import { SOPAgreementType } from "Components/SopCalculator/interfaces";
import { useEffect, useMemo, useState } from "react";
import { isDefined, isUndefined } from "Utils/functions";

export interface SOPRecommendedValues {
  minValue: number | undefined;
  maxValue: number | undefined;
  recommendedDailyPayout: number | undefined;
}

export const MAX_DAILY_PAYOUT = 3300;
const FIVE_HUNDRED_THOUSAND = 500000;
const ONE_AND_A_HALF_MILLION = 1500000;

export function useCalculateRecommendedValues(
  agreementType: SOPAgreementType | undefined,
  yearlyEarning: number | undefined,
): SOPRecommendedValues {
  const [minValue, setMinValue] = useState<number | undefined>(undefined);
  const [maxValue, setMaxValue] = useState<number | undefined>(undefined);
  const [recommendedDailyPayout, setRecommendedDailyPayout] = useState<
    number | undefined
  >(yearlyEarning);

  const calculatedValues = useMemo(() => {
    if (isUndefined(yearlyEarning)) {
      return [undefined, undefined];
    }
    switch (agreementType) {
      case SOPAgreementType.GENERAL_PRACTITIONER:
        if (yearlyEarning <= FIVE_HUNDRED_THOUSAND) {
          return [100, 1500];
        } else if (yearlyEarning <= ONE_AND_A_HALF_MILLION) {
          return [1500, 2500];
        } else {
          return [2000, MAX_DAILY_PAYOUT];
        }
      case SOPAgreementType.SPECIALIST_WITH_OPERATING_AGREEMENT:
        if (yearlyEarning <= ONE_AND_A_HALF_MILLION) {
          return [2000, 3000];
        } else {
          return [2500, MAX_DAILY_PAYOUT];
        }
      case SOPAgreementType.WITHOUT_AGREEMENT:
        if (yearlyEarning <= FIVE_HUNDRED_THOUSAND) {
          return [100, 1500];
        } else if (yearlyEarning <= ONE_AND_A_HALF_MILLION) {
          return [1500, 2500];
        } else {
          return [2500, MAX_DAILY_PAYOUT];
        }
      case SOPAgreementType.SUBSTITUTE:
      default:
        return [undefined, undefined];
    }
  }, [agreementType, yearlyEarning]);

  useEffect(() => {
    const [min, max] = calculatedValues;
    setMinValue(min);
    setMaxValue(max);
    if (isDefined(max)) {
      const recommendedDailyPayout = Math.round((yearlyEarning ?? 0) / 365);
      setRecommendedDailyPayout(
        recommendedDailyPayout > MAX_DAILY_PAYOUT
          ? MAX_DAILY_PAYOUT
          : recommendedDailyPayout,
      );
    } else {
      setRecommendedDailyPayout(max);
    }
  }, [calculatedValues, agreementType, yearlyEarning]);

  return { minValue, maxValue, recommendedDailyPayout };
}
